import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { phoneNumberToWhatsAppJid } from '../../lib/whatsapp/jid';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const getApiUrl = (path: string) => {
  return API_BASE_URL ? new URL(path, API_BASE_URL).toString() : path;
};
export function AddClient() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [showSuccess, setShowSuccess] = useState(false);
  const [savedClientId, setSavedClientId] = useState('1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    type: 'error' | 'info';
    text: string;
  } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    phone: ''
  });
  const redirectTimeout = useRef<ReturnType<typeof setTimeout>>();
  const toastTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
      if (toastTimeout.current) {
        clearTimeout(toastTimeout.current);
      }
    };
  }, []);

  const showToast = (message: string, type: 'error' | 'info') => {
    if (toastTimeout.current) {
      clearTimeout(toastTimeout.current);
    }
    setToast({ type, text: message });
    toastTimeout.current = setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  const handleStartChat = () => {
    const jid = phoneNumberToWhatsAppJid(formData.phone);
    if (!jid) {
      navigate(`/client/${savedClientId}`, { replace: true });
      return;
    }
    navigate(`/whatsapp/chat/${encodeURIComponent(jid)}`, {
      replace: true, // Replace /add-client in history so back button skips it
      state: {
        clientId: savedClientId,
        clientName: formData.name,
        clientPhone: formData.phone,
      },
    });
  };

  const validateForm = () => {
    const newErrors = {
      name: '',
      phone: ''
    };
    let isValid = true;
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
      isValid = false;
    } else if (!/^\+?\d{10,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Enter a valid phone number';
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(getApiUrl('/api/clients'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : {})
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          phone: formData.phone.trim()
        })
      });
      const data = await response.json();

      if (!response.ok) {
        showToast(data?.error || 'Failed to save client. Please try again.', 'error');
        return;
      }

      if (data.duplicate && data.client) {
        showToast(
          data.message || 'Client already exists. Redirecting you now...',
          'info'
        );

        redirectTimeout.current = setTimeout(() => {
          navigate(`/client/${data.client.id}`);
        }, 1200);
        return;
      }

      if (data.client?.id) {
        setSavedClientId(data.client.id);
        setFormData(prev => ({
          ...prev,
          name: data.client.name,
          phone: data.client.phone
        }));
        setShowSuccess(true);
      } else {
        showToast('Unexpected response from server.', 'error');
      }
    } catch (error) {
      console.error('Save client error:', error);
      showToast('Network error. Please check your connection and try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
  const isFormValid = Boolean(formData.name.trim() && formData.phone.trim());
  if (showSuccess) {
    return <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md w-full animate-scale-in">
          <CheckCircleIcon className="w-20 h-20 text-[#5B8E87] mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-[#212121] mb-2">
            Client Added
          </h2>
          <p className="text-gray-600 mb-8">
            {formData.name} has been added to your clients.
          </p>
          <div className="space-y-3">
            <button onClick={handleStartChat} className="w-full bg-[#5B8E87] text-white font-semibold py-4 rounded-xl hover:bg-[#4F7A74] transition-colors">
              Start Chat
            </button>
            <button onClick={() => navigate('/clients')} className="w-full bg-gray-100 text-[#212121] font-semibold py-4 rounded-xl hover:bg-gray-200 transition-colors">
              Go to Clients
            </button>
          </div>
        </div>
      </div>;
  }

  return <div className="min-h-screen bg-[#F5F5F5]">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="flex items-center p-4">
          <button onClick={() => navigate(-1)} className="mr-3 text-gray-600 hover:text-gray-800">
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-semibold text-[#212121]">Add Client</h1>
        </div>
      </div>
      <div className="p-4 pb-24">
        <div className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={e => {
                setFormData({
                  ...formData,
                  name: e.target.value
                });
                if (errors.name) setErrors({
                  ...errors,
                  name: ''
                });
              }}
              placeholder="Enter client name"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={e => {
                setFormData({
                  ...formData,
                  phone: e.target.value
                });
                if (errors.phone) setErrors({
                  ...errors,
                  phone: ''
                });
              }}
              placeholder="+91 98765 43210"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            <p className="text-xs text-gray-500 mt-1">
              Include country code (e.g., +91 98765 43210)
            </p>
          </div>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <button onClick={handleSave} disabled={!isFormValid || isSubmitting} className={`w-full font-semibold py-4 rounded-xl transition-colors ${isFormValid && !isSubmitting ? 'bg-[#5B8E87] text-white hover:bg-[#4F7A74]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
          {isSubmitting ? 'Saving...' : 'Save Client'}
        </button>
      </div>
      {toast && <div className="fixed bottom-24 left-1/2 -translate-x-1/2 px-4">
          <div className={`shadow-lg rounded-2xl px-4 py-3 text-sm font-medium ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-[#5B8E87] text-white'}`}>
            {toast.text}
          </div>
        </div>}
    </div>;
}
